import sriManifest from '~~/sri-manifest.json'

import { sriConfig } from '~/sri.config'
import { customIdleCallback } from '~/utils/requestIdleCallback'

export enum LoadScriptError {
  General = 'load.script.error',
}

export type LoadScriptArguments = {
  options?: {
    appendToBody?: boolean
    async?: boolean
    customAdd?: (element: HTMLScriptElement) => void
    defer?: boolean
    id?: string
    integrity?: string
    onerror?: (error?: Error) => void
    onload?: (event: Event) => void
  }
  src: string
}

export const loadScript = (loadScript: LoadScriptArguments, timeout?: number) =>
  customIdleCallback(() => scriptGenerator(loadScript), { timeout })

export const scriptGenerator = (loadScriptArgs: LoadScriptArguments) => {
  const { options, src } = loadScriptArgs
  return new Promise<Maybe<HTMLScriptElement>>((resolve) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const element = document.createElement('script')
    element.setAttribute('nonce', useSafeNonce())
    const { mappings: sriMappings } = sriConfig

    const hasSri = !!(sriManifest.scripts as Record<string, string>)?.[src]

    const targetSrc = hasSri
      ? (sriMappings as Record<string, string>)?.[src] || src
      : src

    if (options?.integrity || hasSri)
      element.setAttribute(
        'integrity',
        options?.integrity ||
          (sriManifest?.scripts as Record<string, string>)?.[src],
      )

    if (options) {
      const { async, defer, id } = options
      if (id) element.setAttribute('id', id)
      element.setAttribute('async', String(async))
      element.setAttribute('defer', String(async ? false : defer))
    }

    element.onerror = (_, __, ___, ____, error) => {
      if (options?.onerror) options.onerror(error)
      resolve(null)
      useDatadog().log('error', {
        component: 'loadScript',
        message: `${LoadScriptError.General} for ${targetSrc}`,
        messageContext: { hasSri, src, targetSrc },
      })
      element.remove()
    }
    element.onload = (event: Event) => {
      if (options?.onload) options.onload(event)
      resolve(element)
    }

    if (options?.customAdd) {
      options.customAdd(element)
      return element
    }

    element.setAttribute('src', String(targetSrc))

    const appendTo = options?.appendToBody ? document.body : document.head
    return appendTo.appendChild(element)
  })
}
